import {
  ActivitySectionsQuery,
  ActivitySectionsQueryVariables,
} from "@outschool/gql-frontend-generated";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { MeetingFragment } from "@outschool/ui-components-classroom";

import * as helpers from "../../shared/helpers";
import useSectionString from "../../shared/hooks/useSectionString";
import { ActivityClubFrequency } from "../../shared/Section";
import { SectionFragment, SectionLeaderFragment } from "./SectionFragments";

const activitySectionsQuery = gql`
  query ActivitySections($uid: ID!) {
    activity(uid: $uid) {
      uid
      paginatedFilteredSections(filter: Upcoming) {
        data {
          filledSpaceCount
          user_uid
          ...SectionFragment
          ...SectionLeaderFragment
          meetings {
            ...MeetingFragment
          }
        }
      }
    }
  }
  ${SectionFragment}
  ${SectionLeaderFragment}
  ${MeetingFragment}
`;

export type TransferSection =
  ActivitySectionsQuery["activity"]["paginatedFilteredSections"]["data"][0];
export function useMapAsOptions(
  activity: ActivityClubFrequency & { hasTeacherSchedule: boolean },
  sections: TransferSection[],
  userTimeZone: string,
) {
  const { fullDurationString, fullDurationStringWithTeacherName } =
    useSectionString();
  return sections.map(section => ({
    label: section.user_uid
      ? fullDurationStringWithTeacherName({
          activity,
          section,
          nextOngoingMeeting: section.nextOngoingMeeting,
          userTimeZone,
        })
      : fullDurationString({
          activity,
          section,
          nextOngoingMeeting: section.nextOngoingMeeting,
          userTimeZone,
        }),
    value: section,
  }));
}

export default function useTransferSections(
  activity: { uid: string },
  section: { uid: string },
) {
  const { data, loading } = useQueryWithPreviousData<
    ActivitySectionsQuery,
    ActivitySectionsQueryVariables
  >(activitySectionsQuery, {
    skip: !helpers.isUuid(activity?.uid),
    variables: {
      uid: activity?.uid,
    },
  });

  const sections =
    (!loading &&
      data?.activity?.paginatedFilteredSections?.data?.filter(
        s => s.allowsTeacherTransfer && s.uid !== section.uid,
      )) ||
    [];
  return { loading, sections };
}
